export const ENCODING_PROFILES = [
  {
    displayName: "English",
    identifier: "english",
    languageCode: "en",
    knownNumberWikipediaIdentity: { kind: "catalog-label" },
    requiresLocalizedCandidateMetadata: false,
    requiresPronunciationMetadata: false,
    showsPronunciationMetadata: true,
    savedAssociationsExportFilename: "nummem-saved-numbers.csv",
    savedAssociationsExportStatus: "Saved numbers exported",
    sharePayloadVersion: 1,
    usesCatalogKnownNumberMetadata: true,
    wikipediaHost: "en.wikipedia.org",
    wikipediaLanguage: "en",
    wikipediaSearchFallback: false,
  },
  {
    displayName: "Russian",
    identifier: "russian",
    languageCode: "ru",
    knownNumberWikipediaIdentity: {
      kind: "wikidata-exact-label-sitelink",
      resolverHost: "www.wikidata.org",
      sourceLanguage: "en",
      targetSite: "ruwiki",
    },
    requiresLocalizedCandidateMetadata: true,
    requiresPronunciationMetadata: false,
    showsPronunciationMetadata: false,
    savedAssociationsExportFilename: "nummem-russian-saved-numbers.csv",
    savedAssociationsExportStatus: "Russian saved numbers exported",
    sharePayloadVersion: 2,
    usesCatalogKnownNumberMetadata: false,
    wikipediaHost: "ru.wikipedia.org",
    wikipediaLanguage: "ru",
    wikipediaSearchFallback: true,
  },
  {
    displayName: "Spanish",
    identifier: "spanish",
    languageCode: "es",
    knownNumberWikipediaIdentity: {
      kind: "wikidata-exact-label-sitelink",
      resolverHost: "www.wikidata.org",
      sourceLanguage: "en",
      targetSite: "eswiki",
    },
    requiresLocalizedCandidateMetadata: true,
    requiresPronunciationMetadata: true,
    showsPronunciationMetadata: true,
    savedAssociationsExportFilename: "nummem-spanish-saved-numbers.csv",
    savedAssociationsExportStatus: "Spanish saved numbers exported",
    sharePayloadVersion: 2,
    usesCatalogKnownNumberMetadata: false,
    wikipediaHost: "es.wikipedia.org",
    wikipediaLanguage: "es",
    wikipediaSearchFallback: true,
  },
] as const;

export type EncodingProfile = (typeof ENCODING_PROFILES)[number];
export type EncodingSystem = EncodingProfile["identifier"];
export type NonEnglishEncodingSystem = Exclude<EncodingSystem, "english">;
export const DEFAULT_ENCODING_SYSTEM = ENCODING_PROFILES[0].identifier;

const ENCODING_PROFILES_BY_IDENTIFIER = new Map<EncodingSystem, EncodingProfile>(
  ENCODING_PROFILES.map((profile) => [profile.identifier, profile]),
);

export function isEncodingSystem(value: unknown): value is EncodingSystem {
  return typeof value === "string" && ENCODING_PROFILES_BY_IDENTIFIER.has(
    value as EncodingSystem,
  );
}

export function encodingSystemOrDefault(value: unknown): EncodingSystem {
  return isEncodingSystem(value) ? value : DEFAULT_ENCODING_SYSTEM;
}

export function isNonEnglishEncodingSystem(
  value: unknown,
): value is NonEnglishEncodingSystem {
  return isEncodingSystem(value) && value !== "english";
}

export function getEncodingProfile(
  encodingSystem: EncodingSystem,
): EncodingProfile {
  return ENCODING_PROFILES_BY_IDENTIFIER.get(encodingSystem)!;
}

export function matchesProfileLanguage(
  value: unknown,
  encodingSystem: EncodingSystem,
): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const languageCode = getEncodingProfile(encodingSystem).languageCode;
  const normalized = value.toLowerCase();
  return normalized === languageCode || normalized.startsWith(`${languageCode}-`);
}

export function matchesProfileWikipediaUrl(
  value: unknown,
  encodingSystem: EncodingSystem,
): value is string | null | undefined {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      url.hostname === getEncodingProfile(encodingSystem).wikipediaHost;
  } catch {
    return false;
  }
}

export function candidateMetadataMatchesProfile(
  candidate: {
    label_lang?: string | null;
    wikipedia_url?: string | null;
  },
  encodingSystem: EncodingSystem,
): boolean {
  const profile = getEncodingProfile(encodingSystem);
  return !profile.requiresLocalizedCandidateMetadata || (
    matchesProfileLanguage(candidate.label_lang, encodingSystem) &&
    matchesProfileWikipediaUrl(candidate.wikipedia_url, encodingSystem)
  );
}
